import { Container } from "@mui/material"
import Box from "@mui/material/Box"
import Grid from "@mui/material/Grid"
import {
  DataGridPro,
  GridColDef,
} from "@mui/x-data-grid-pro"
import { DateRange, DateRangePicker } from "@mui/x-date-pickers-pro"
import { dataGridDEde } from "constants/dataGridLocale"
import { datePickerLocaleDEde } from "constants/datePickerLocale"
import dayjs, { Dayjs } from "dayjs"
import { SalesStat } from "generated/graphql"
import { uniqBy } from "lodash"
import { fetchSaleStats } from "queries/fetchSalesStats"
import { useEffect, useState } from "react"
import { formatDateFromDate } from "utils/formatDate"
import { shortcutsItems } from "utils/shortcutItems"

const columns: GridColDef<SalesStat>[] = [
  {
    field: "artnum",
    headerName: "Artikel Nr.",
    width: 100,
    editable: false,
  },
  {
    field: "pflNR",
    headerName: "PFL Nr.",
    width: 100,
    editable: false,
  },
  {
    field: "gername",
    headerName: "Name",
    width: 250,
    editable: false,
  },
  {
    field: "botname",
    headerName: "Botanischer Name",
    width: 250,
    editable: false,
  },
  {
    field: "from",
    headerName: "Von",
    width: 80,
    editable: false,
  },
  {
    field: "to",
    headerName: "Bis",
    width: 80,
    editable: false,
  },
  {
    field: "price",
    headerName: "Preis",
    width: 150,
    editable: false,
    valueFormatter: (value,row) => {
      if (!row.price || row.price === 0) return "0,00 €"
      return row.price.toLocaleString("de", {
        maximumFractionDigits: 2,
        style: "currency",
        currency: "EUR",
      })
    }
  },
  {
    field: "count",
    headerName: "Anzahl",
    width: 80,
    editable: false,
  },
]

const SalesStatsPage = () => {
  const [loading, setloading] = useState(false)
  const [dateRange, setDateRange] = useState<DateRange<Dayjs>>([
    dayjs().startOf("month"),
    dayjs(),
  ])

  const [saleStatsData, setSaleStatsData] = useState<SalesStat[]>([])

  useEffect(() => {
    setloading(true)
    const fromDateString = dateRange[0]
      ? formatDateFromDate(dateRange[0].toDate())
      : ""
    const toDateString = dateRange[1]
      ? formatDateFromDate(dateRange[1].toDate())
      : ""
    if (!fromDateString || !toDateString) return
    if (fromDateString === "" || toDateString === "") return
    fetchSaleStats(fromDateString, toDateString).then((res) => {
      if (res) {
        setloading(false)
        // sort by price descending
        setSaleStatsData(res.salesStats.sort((a, b) => b.price - a.price))
      }
    })
  }, [dateRange, setloading, setSaleStatsData])

  const calculateSummary = (sd: SalesStat[]): SalesStat => {
    const summary: SalesStat = {
      artnum: 0,
      pflNR: 0, // count diff pflnr
      gername: `Gesamt: Artikel: ${uniqBy(sd, "artnum").length}`,
      botname: `Pflanzen: ${uniqBy(sd, "pflNR").length}`,
      from: 0,
      to: 0,
      price: sd.reduce((acc, curr) => acc + curr.price, 0),
      count: sd.reduce((acc, curr) => acc + curr.count, 0),
    }
    return summary
  }

  return (
    <Container maxWidth="xl">
      <Grid container spacing={1}>
        <Grid size={12}>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
              <DateRangePicker
                slotProps={{
                  shortcuts: {
                    items: shortcutsItems,
                  },
                }}
                localeText={datePickerLocaleDEde}
                value={dateRange}
                onChange={(newValue) => setDateRange(newValue)}
                disableFuture={true}
              />
          </Box>
        </Grid>
        <Grid size={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                height: "calc(100vh - 20vh)",
                width: "100%",
              }}
            >
              <DataGridPro
                rows={saleStatsData}
                pinnedRows={{
                  top: [calculateSummary(saleStatsData)],
                }}
                columns={columns}
                localeText={dataGridDEde}
                getRowId={(item) => item.artnum}
                loading={loading}
                sortingOrder={["desc", "asc"]}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Container>
  )
}

export default SalesStatsPage
